// RdvAffaire.js
import React, { useState } from "react";
import { API_BASE } from "../config";
import ChoisirCreneauxRdv from "../app/newcalendar/ChoisirCreneauxRdv";
import { CalendarDateRangeIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";

// ---------- UI helpers ----------
const Badge = ({ children, variant }) => {
  const classes =
    variant === "orange"
      ? "inline-flex items-center rounded-md bg-orange-100 px-2 py-1 text-xs font-bold text-orange-800"
      : variant === "green"
      ? "inline-flex items-center rounded-md bg-emerald-100 px-2 py-1 text-xs font-bold text-emerald-800"
      : "inline-flex items-center rounded-md bg-slate-100 px-2 py-1 text-xs font-bold text-slate-800";

  return <span className={classes}>{children}</span>;
};

const TypeReunionLabel = ({ value }) => {
  if (value === "A l'étude") return <>A l'étude</>;
  if (value === "Téléphone") return <>Par téléphone</>;
  if (value === "Visio") return <>En visio</>;
  return <></>;
};

const parseDispoString = (str) => {
  if (!str) return null;
  // Format attendu : "30/05/2026 - 10:00 à 11:00"
  const match = str.match(/^(\d{2})\/(\d{2})\/(\d{4})\s*-\s*(.+)$/);
  if (!match) return { raw: str };
  const [, day, month, year, timeRange] = match;
  const date = new Date(Number(year), Number(month) - 1, Number(day));
  const dayName = date.toLocaleDateString("fr-FR", { weekday: "long" });
  return {
    dayName: dayName.charAt(0).toUpperCase() + dayName.slice(1),
    date: `${day}/${month}/${year}`,
    timeRange: timeRange.trim(),
  };
};

const DispoCards = ({ rdv }) => {
  const f = rdv?.fields ?? {};
  const dispos = [f.dispo_notaire_1, f.dispo_notaire_2, f.dispo_notaire_3].filter(Boolean);
  if (dispos.length === 0) return null;

  return (
    <div className="flex flex-row gap-2 flex-wrap">
      {dispos.map((d, i) => {
        const parsed = parseDispoString(d);
        return (
          <div key={i} className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-center min-w-[130px]">
            <p className="text-xs font-medium text-indigo-400 uppercase tracking-wide m-0">{parsed?.dayName ?? d}</p>
            <p className="text-sm font-bold text-gray-900 m-0 mt-1">{parsed?.date ?? ""}</p>
            <p className="text-xs text-indigo-600 font-semibold m-0 mt-1">{parsed?.timeRange ?? ""}</p>
          </div>
        );
      })}
    </div>
  );
};

const ConfirmCancelModal = ({ open, onClose, onConfirm, isLoading }) => {
  return (
    <Dialog open={open} onClose={onClose} className="relative z-10">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in dark:bg-gray-900/50"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95 dark:bg-gray-800 dark:outline dark:-outline-offset-1 dark:outline-white/10"
          >
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10 dark:bg-red-500/10">
                <ExclamationTriangleIcon aria-hidden="true" className="size-6 text-red-600 dark:text-red-400" />
              </div>

              <div className="text-left sm:mt-0 sm:ml-4 sm:text-left">
                <DialogTitle as="h3" className="text-base font-semibold text-gray-900 dark:text-white">
                  Supprimer la proposition de créneaux
                </DialogTitle>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Êtes-vous sûr de vouloir supprimer ces propositions ? Le rendez-vous repassera à{" "}
                    <b>“à programmer”</b>.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-3 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={onConfirm}
                disabled={isLoading}
                className={
                  isLoading
                    ? "mb-0 inline-flex w-full justify-center rounded-md bg-red-600/60 px-3 py-2 text-sm font-semibold text-white shadow-xs sm:ml-3 sm:w-auto cursor-not-allowed"
                    : "mb-0 inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto dark:bg-red-500 dark:shadow-none dark:hover:bg-red-400"
                }
              >
                {isLoading ? "Suppression..." : "Oui, supprimer"}
              </button>

              <button
                type="button"
                data-autofocus
                onClick={onClose}
                disabled={isLoading}
                className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring-1 inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto dark:bg-white/10 dark:text-white dark:shadow-none dark:inset-ring-white/5 dark:hover:bg-white/20"
              >
                Non, annuler
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

// ---------- core block ----------
const RdvBlock = ({ title, rdv, type, idAffaire, notaireAirtableId, refreshData }) => {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [openCancelRdv, setOpenCancelRdv] = useState(false);
  const [isCancellingRdv, setIsCancellingRdv] = useState(false);

  if (!rdv) return null;

  const statutRaw = String(rdv?.fields?.statut || "").trim().toLowerCase();
  const typeReunion = rdv?.fields?.type_reunion;

  const isEnCours = statutRaw === "en cours de prog";
  const isProgramme = statutRaw === "programme";

  // ✅ on accepte les 2 écritures
  const isAProgrammer = statutRaw === "a programmer" || statutRaw === "à programmer";

  const doCancel = async () => {
    if (!rdv?.id) return;

    setIsCancelling(true);
    try {
      // prod
      const res = await fetch(`${API_BASE}/api/cancel-meeting-proposal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_rdv: rdv.id }),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status} - ${txt}`);
      }

      setOpenConfirm(false);

      if (typeof refreshData === "function") {
        await refreshData();
      } else {
        console.warn("refreshData non fourni: impossible de rafraîchir l'affichage sans reload.");
      }
    } catch (e) {
      console.error("cancel-meeting-proposal error", e);
      alert("Impossible d'annuler la proposition. Réessaie.");
    } finally {
      setIsCancelling(false);
    }
  };

  if (isEnCours) {
    const dateProp = rdv?.fields?.date_proposition
      ? new Date(rdv.fields.date_proposition).toLocaleString("fr-FR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;

    return (
      <>
        <div className="mt-2 bg-white rounded-lg p-4 border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CalendarDateRangeIcon className="size-5 text-gray-400" />
              <h5 className="font-bold text-gray-900 m-0">{title}</h5>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-orange-50 px-2.5 py-1 text-xs font-semibold text-orange-700 border border-orange-100">
              <span className="size-1.5 rounded-full bg-orange-500 inline-block"></span>
              En attente de confirmation
            </span>
          </div>

          {dateProp && (
            <p className="text-xs  mb-3 m-0">
              Proposé le {dateProp}
              {typeReunion && <> · <TypeReunionLabel value={typeReunion} /></>}
            </p>
          )}
          <DispoCards rdv={rdv} />

          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={() => setOpenConfirm(true)}
              className="text-xs text-red-600 hover:text-red-800 font-medium underline"
            >
              Annuler la proposition
            </button>
          </div>
        </div>

        <ConfirmCancelModal
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          onConfirm={doCancel}
          isLoading={isCancelling}
        />
      </>
    );
  }

  if (isProgramme) {
    const dateFinale = rdv?.fields?.date_finale
      ? new Date(rdv.fields.date_finale).toLocaleString("fr-FR", {
          weekday: "long",
          day: "2-digit",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;

    const doCancelRdv = async () => {
      setIsCancellingRdv(true);
      try {
        const res = await fetch(`${API_BASE}/api/cancel-confirmed-rdv`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_rdv: rdv.id }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        setOpenCancelRdv(false);
        if (typeof refreshData === "function") await refreshData();
      } catch (e) {
        console.error("cancelConfirmedRdv error", e);
        alert("Impossible d'annuler le rendez-vous. Réessaie.");
      } finally {
        setIsCancellingRdv(false);
      }
    };

    return (
      <>
        <div className="mt-2 bg-white rounded-lg p-4 border border-emerald-100">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <CalendarDateRangeIcon className="size-5 text-gray-400" />
              <h5 className="font-bold text-gray-900 m-0">{title}</h5>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 border border-emerald-100">
              <span className="size-1.5 rounded-full bg-emerald-500 inline-block"></span>
              Confirmé
            </span>
          </div>

          <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3">
            <CalendarDateRangeIcon className="size-5 text-emerald-600 shrink-0" />
            <div>
              <p className="text-sm font-bold text-emerald-900 m-0 capitalize">
                {dateFinale ?? "Date non renseignée"}
              </p>
              {typeReunion && (
                <p className="text-xs text-emerald-600 m-0 mt-0.5">
                  <TypeReunionLabel value={typeReunion} />
                </p>
              )}
            </div>
          </div>

          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={() => setOpenCancelRdv(true)}
              className="text-xs text-red-600 hover:text-red-800 font-medium underline"
            >
              Annuler le rendez-vous
            </button>
          </div>
        </div>

        {/* Modal confirmation annulation RDV */}
        <Dialog open={openCancelRdv} onClose={() => setOpenCancelRdv(false)} className="relative z-10">
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
          />
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <DialogPanel
                transition
                className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95"
              >
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10">
                    <ExclamationTriangleIcon aria-hidden="true" className="size-6 text-red-600" />
                  </div>
                  <div className="text-left sm:mt-0 sm:ml-4 sm:text-left">
                    <DialogTitle as="h3" className="text-base font-semibold text-gray-900">
                      Annuler ce rendez-vous ?
                    </DialogTitle>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Le rendez-vous sera annulé et tous les participants seront notifiés par email. Le RDV repassera en "à programmer".
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={doCancelRdv}
                    disabled={isCancellingRdv}
                    className={isCancellingRdv
                      ? "mb-0 inline-flex w-full justify-center rounded-md bg-red-600/60 px-3 py-2 text-sm font-semibold text-white sm:ml-3 sm:w-auto cursor-not-allowed"
                      : "mb-0 inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-500 sm:ml-3 sm:w-auto"
                    }
                  >
                    {isCancellingRdv ? "Annulation..." : "Oui, annuler le RDV"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setOpenCancelRdv(false)}
                    disabled={isCancellingRdv}
                    className="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring-1 inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Non, conserver
                  </button>
                </div>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      </>
    );
  }

  if (isAProgrammer) {
    return (
      <div className="mt-2 bg-white rounded-lg p-4 border border-gray-100">
        <div className="flex items-center justify-between mb-0">
          <div className="flex items-center gap-2">
            <CalendarDateRangeIcon className="size-5 text-gray-400" />
            <h5 className="font-bold text-gray-900 m-0">{title}</h5>
          </div>
          <ChoisirCreneauxRdv
            where="header"
            id_transaction={idAffaire}
            type={type}
            id_rdv={rdv.id}
            notaireAirtableId={notaireAirtableId}
            refreshData={refreshData}
          />
        </div>
      </div>
    );
  }

  return null;
};

// ---------- export ----------
const RdvAffaire = ({ idAffaire, type, notaireAirtableId, rdvNotaire, rdvSignature, refreshData }) => {
  return (
    <>
      <RdvBlock
        title="Rendez-vous notaire"
        rdv={rdvNotaire}
        type={type}
        idAffaire={idAffaire}
        notaireAirtableId={notaireAirtableId}
        refreshData={refreshData}
      />

      <RdvBlock
        title="Rendez-vous de Signature"
        rdv={rdvSignature}
        type={type}
        idAffaire={idAffaire}
        notaireAirtableId={notaireAirtableId}
        refreshData={refreshData}
      />
    </>
  );
};

export default RdvAffaire;